import type { AuthSession } from "./types";
import { authenticatedFetch } from "./auth";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string | undefined;

export type WhatsAppGroup = {
  groupId: string;
  connectionId: string;
  phoneNumberId: string;
  subject: string;
  description?: string;
  status: string;
  joinApprovalMode?: string;
  participantCount?: number;
  participants?: unknown[];
  providerRequestId?: string;
  resolvedGroupId?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type GroupEligibility = {
  eligible: boolean;
  reason?: string;
  providerCode?: number;
  connectionId: string;
  phoneNumberId?: string;
};

export type GroupJoinRequest = {
  phone: string;
  requestedAt?: string;
  raw?: unknown;
};

export async function listRemoteWhatsAppGroups(
  session: AuthSession,
  connectionId?: string,
): Promise<WhatsAppGroup[]> {
  const query = connectionId ? `?connectionId=${encodeURIComponent(connectionId)}` : "";
  const response = await request<{ items: WhatsAppGroup[] }>(`/whatsapp/groups${query}`, session);
  return response.items;
}

export async function checkRemoteWhatsAppGroupsEligibility(
  connectionId: string,
  session: AuthSession,
): Promise<GroupEligibility> {
  return request<GroupEligibility>("/whatsapp/groups/eligibility", session, {
    method: "POST",
    body: JSON.stringify({ connectionId }),
  });
}

export async function syncRemoteWhatsAppGroups(connectionId: string, session: AuthSession) {
  return request<{ items: WhatsAppGroup[]; synced: number }>("/whatsapp/groups/sync", session, {
    method: "POST",
    body: JSON.stringify({ connectionId }),
  });
}

export async function createRemoteWhatsAppGroup(
  input: {
    connectionId: string;
    subject: string;
    description?: string;
    joinApprovalMode: "approval_required" | "auto_approve";
  },
  session: AuthSession,
) {
  const response = await request<{ item: WhatsAppGroup }>("/whatsapp/groups", session, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return response.item;
}

export async function updateRemoteWhatsAppGroup(
  groupId: string,
  input: { subject?: string; description?: string; joinApprovalMode?: "approval_required" | "auto_approve" },
  session: AuthSession,
) {
  const response = await request<{ item: WhatsAppGroup }>(groupPath(groupId), session, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
  return response.item;
}

export async function getRemoteWhatsAppGroupInviteLink(groupId: string, session: AuthSession) {
  return request<{ inviteLink: string }>(`${groupPath(groupId)}/invite-link`, session);
}

export async function resetRemoteWhatsAppGroupInviteLink(groupId: string, session: AuthSession) {
  return request<{ inviteLink: string }>(`${groupPath(groupId)}/invite-link/reset`, session, { method: "POST" });
}

export async function listRemoteWhatsAppGroupJoinRequests(groupId: string, session: AuthSession) {
  const response = await request<{ items: GroupJoinRequest[] }>(`${groupPath(groupId)}/join-requests`, session);
  return response.items;
}

export async function approveRemoteWhatsAppGroupJoinRequests(
  groupId: string,
  phones: string[],
  session: AuthSession,
) {
  return request<{ approved: number }>(`${groupPath(groupId)}/join-requests/approve`, session, {
    method: "POST",
    body: JSON.stringify({ phones }),
  });
}

export async function rejectRemoteWhatsAppGroupJoinRequests(
  groupId: string,
  phones: string[],
  session: AuthSession,
) {
  return request<{ rejected: number }>(`${groupPath(groupId)}/join-requests/reject`, session, {
    method: "POST",
    body: JSON.stringify({ phones }),
  });
}

export async function removeRemoteWhatsAppGroupParticipants(
  groupId: string,
  phones: string[],
  session: AuthSession,
) {
  return request<{ removed: number }>(`${groupPath(groupId)}/participants/remove`, session, {
    method: "POST",
    body: JSON.stringify({ phones }),
  });
}

export async function sendRemoteWhatsAppGroupMessage(groupId: string, body: string, session: AuthSession) {
  return request<{ item: Record<string, unknown> }>(`${groupPath(groupId)}/messages`, session, {
    method: "POST",
    body: JSON.stringify({ body }),
  });
}

function groupPath(groupId: string) {
  return `/whatsapp/groups/${encodeURIComponent(groupId)}`;
}

async function request<T>(path: string, session: AuthSession, init: RequestInit = {}): Promise<T> {
  if (!API_BASE_URL) throw new Error("API_NOT_CONFIGURED");
  const response = await authenticatedFetch(`${API_BASE_URL}${path}`, session, init);
  const value = await response.json().catch(() => ({}));
  if (!response.ok) {
    const code = typeof value?.error === "string" ? value.error : `HTTP_${response.status}`;
    const providerCode = Number(value?.providerCode);
    throw new Error(Number.isFinite(providerCode) && providerCode > 0 ? `${code}::${providerCode}` : code);
  }
  return value as T;
}
